import { useEffect, useState } from 'react'
import { Moon, Sun } from './icons'

const SECTIONS = [
  { id: 'work', label: 'Products' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
]

/** Reads the stored theme, falling back to whatever the OS reports. */
const readTheme = () => {
  try {
    const stored = window.localStorage.getItem('theme')
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* private mode / blocked storage — fall through to the system preference */
  }
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark'
}

export const Navigation = ({ company }) => {
  const [stuck, setStuck] = useState(false)
  const [active, setActive] = useState('')
  const [theme, setTheme] = useState(readTheme)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      window.localStorage.setItem('theme', theme)
    } catch {
      /* not persisting is acceptable; the page still renders correctly */
    }
  }, [theme])

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Highlight whichever section currently owns the upper half of the viewport.
  useEffect(() => {
    const targets = SECTIONS.map(({ id }) => document.getElementById(id)).filter(
      Boolean
    )
    if (!targets.length || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible) setActive(visible.target.id)
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.1, 0.5] }
    )

    targets.forEach((t) => observer.observe(t))
    return () => observer.disconnect()
  }, [])

  const nextTheme = theme === 'dark' ? 'light' : 'dark'

  return (
    <nav className="nav" data-stuck={stuck} aria-label="Primary">
      <div className="nav__inner">
        <a className="nav__brand" href="#top">
          <span className="nav__monogram" aria-hidden="true">
            {company.initials}
          </span>
          <span>{company.name}</span>
        </a>

        <ul className="nav__links">
          {SECTIONS.map(({ id, label }) => (
            <li key={id}>
              <a
                className="nav__link"
                href={`#${id}`}
                aria-current={active === id ? 'true' : undefined}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>

        <div className="nav__actions">
          <button
            type="button"
            className="icon-btn"
            onClick={() => setTheme(nextTheme)}
            aria-label={`Switch to ${nextTheme} theme`}
            title={`Switch to ${nextTheme} theme`}
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </button>
        </div>
      </div>
    </nav>
  )
}
